// ChartDataService.ts - A service to manage chart data requests with staggered loading
import { ReferralsApi } from './ApiWrappers'

// Define the types for chart data requests
type chartType =
  | 'cases_created'
  | 'referralstotal'
  | 'hirestartsbymonth'
  | 'referral_and_hire_counts'
  | 'conversion_rate'
  | 'cases_sent_last_28_days'
  | 'cases_created_dates'
  | 'claimscorebymonth'
  | 'conversion_claim_score'

interface ChartDataRequest {
  chartType: string
  partnertype: string
  priority: number // Lower number = higher priority
  callback: (data: any) => void
}

// Cache stored chart data
interface CacheEntry {
  data: any
  timestamp: number
  partnertype: string
}

export type CasesSentData = Array<{
  partner_name: string
  sent_count: number
  converted_count: number
  conversion_rate: number
}>

class ChartDataService {
  private requestQueue: ChartDataRequest[] = []
  private isProcessing = false
  private cache: Record<string, CacheEntry> = {}
  private cacheDuration: number = 5 * 60 * 1000 // 5 minutes cache duration
  private requestDelay = 100 // 100ms delay between requests (reduced from 300ms)
  private maxParallelRequests = 3 // Allow up to 3 parallel requests for high-priority charts
  private activeRequests = 0

  // Add a chart data request to the queue
  public requestChartData(
    chartType: string,
    partnertype: string,
    callback: (data: any) => void,
    priority = 5,
  ) {
    // Check if we have valid cached data
    const cacheKey = `${chartType}:${partnertype}`
    const cachedEntry = this.cache[cacheKey]

    if (
      cachedEntry &&
      Date.now() - cachedEntry.timestamp < this.cacheDuration &&
      cachedEntry.partnertype === partnertype
    ) {
      // Use cached data if it's still fresh
      console.log(`Using cached data for ${chartType}`)
      setTimeout(() => callback(cachedEntry.data), 0)
      return
    }

    // Add request to queue if not already in queue
    const existingRequest = this.requestQueue.find(
      (req) => req.chartType === chartType && req.partnertype === partnertype,
    )

    if (existingRequest) {
      // Update the callback if request is already in queue
      existingRequest.callback = callback
      existingRequest.priority = Math.min(existingRequest.priority, priority)
    } else {
      // Add new request to queue
      this.requestQueue.push({
        chartType: chartType,
        partnertype: partnertype,
        callback,
        priority,
      })

      // Sort the queue by priority (lower number = higher priority)
      this.requestQueue.sort((a, b) => a.priority - b.priority)

      // Start processing if not already processing
      if (!this.isProcessing) {
        this.processNextRequest()
      }
    }
  }

  // Process the next request in the queue
  private processNextRequest() {
    if (this.requestQueue.length === 0) {
      this.isProcessing = false
      return
    }

    this.isProcessing = true
    const request = this.requestQueue.shift()

    if (!request) {
      this.isProcessing = false
      return
    }

    console.log(`Processing chart data request for ${request.chartType}`)

    // Add instrumentation to measure request time
    const startTime = Date.now()

    // Increment active requests counter
    this.activeRequests++

    // Fetch data from the API
    new ReferralsApi()
      .referralChartData({
        chartType: request.chartType,
        partnertype: request.partnertype,
      })
      .then((response: any) => {
        // Cache the response
        this.cache[`${request.chartType}:${request.partnertype}`] = {
          data: response,
          timestamp: Date.now(),
          partnertype: request.partnertype,
        }

        // Call the callback with the data
        request.callback(response)

        // Log request time for monitoring
        console.log(
          `Chart request for ${request.chartType} completed in ${Date.now() - startTime}ms`,
        )
      })
      .catch((error: any) => {
        console.error(
          `Error fetching chart data for ${request.chartType}:`,
          error,
        )
        // Call callback with null to indicate error
        request.callback(null)
      })
      .finally(() => {
        // Decrement active requests counter
        this.activeRequests--

        // Process the next request after a delay
        // For high-priority requests (priority 1-3), allow parallel processing
        const canProcessParallel =
          this.activeRequests < this.maxParallelRequests &&
          this.requestQueue.length > 0 &&
          this.requestQueue[0].priority <= 3

        if (canProcessParallel) {
          // Process immediately for high-priority charts
          setTimeout(() => {
            this.processNextRequest()
          }, 50) // Minimal delay for parallel high-priority requests
        } else {
          // Regular delay for lower-priority requests
          setTimeout(() => {
            this.processNextRequest()
          }, this.requestDelay)
        }
      })
  }

  // Clear the cache for testing or when user data changes
  public clearCache() {
    this.cache = {}
  }
}

// Create and export a singleton instance
const chartDataService = new ChartDataService()
export default chartDataService
